import { useState, useEffect } from 'react';
import { PredictionProvider, usePredictions } from './context/PredictionContext';
import { useBracketData } from './hooks/useBracketData';
import { GroupStage } from './components/GroupStage';
import { BracketCanvas } from './components/BracketCanvas';
import { MatchList } from './components/MatchList';

type ViewMode = 'bracket' | 'matches';

function AppContent() {
  const { data, loading, error } = useBracketData();
  const { clearAllPredictions, setDefaultGroupStandings } = usePredictions();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('bracket');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Initialize default group standings when data loads
  useEffect(() => {
    if (data?.groups) {
      setDefaultGroupStandings(data.groups);
    }
  }, [data?.groups, setDefaultGroupStandings]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-fifa-blue border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tournament data...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <p className="text-red-600 mb-2">Failed to load tournament data</p>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-fifa-dark text-white px-3 sm:px-4 py-2 sm:py-3 shrink-0">
        {/* Mobile Header */}
        <div className="flex items-center justify-between">
          {/* Logo/Title */}
          <div className="flex items-center gap-2">
            <h1 className="text-base sm:text-xl font-bold whitespace-nowrap">FIFA World Cup 2026</h1>
            <span className="hidden sm:inline text-sm text-gray-300">Bracket Predictor</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1 bg-fifa-blue/30 rounded-lg p-1">
            <button
              onClick={() => setViewMode('bracket')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'bracket'
                  ? 'bg-white text-fifa-dark'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Bracket
            </button>
            <button
              onClick={() => setViewMode('matches')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'matches'
                  ? 'bg-white text-fifa-dark'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              All Matches
            </button>
          </div>

          {/* Desktop Stats & Reset */}
          <div className="hidden md:flex items-center gap-4">
            <span className="text-sm text-gray-300">
              48 Teams • 104 Matches • 16 Cities
            </span>
            <button
              onClick={clearAllPredictions}
              className="text-sm px-3 py-1 bg-red-600/20 text-red-300 rounded hover:bg-red-600/30 transition-colors"
            >
              Reset
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-3 pt-3 border-t border-white/20 space-y-3">
            {/* Mobile Navigation Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setViewMode('bracket');
                  setMobileMenuOpen(false);
                }}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'bracket'
                    ? 'bg-white text-fifa-dark'
                    : 'bg-fifa-blue/30 text-white'
                }`}
              >
                Bracket
              </button>
              <button
                onClick={() => {
                  setViewMode('matches');
                  setMobileMenuOpen(false);
                }}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'matches'
                    ? 'bg-white text-fifa-dark'
                    : 'bg-fifa-blue/30 text-white'
                }`}
              >
                All Matches
              </button>
            </div>

            {/* Mobile Stats & Actions */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-300">
                48 Teams • 104 Matches • 16 Cities
              </span>
              <button
                onClick={() => {
                  clearAllPredictions();
                  setMobileMenuOpen(false);
                }}
                className="text-xs px-3 py-1.5 bg-red-600/20 text-red-300 rounded hover:bg-red-600/30 transition-colors"
              >
                Reset Predictions
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      {viewMode === 'bracket' ? (
        <div className="flex flex-1 overflow-hidden">
          {/* Group Stage Sidebar */}
          <GroupStage
            groups={data.groups}
            teams={data.teams}
            venues={data.venues}
            groupMatches={data.groupMatches}
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          />

          {/* Knockout Bracket */}
          <div className="flex-1 overflow-hidden">
            <BracketCanvas
              matches={data.knockoutMatches}
              teams={data.teams}
              venues={data.venues}
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto bg-slate-100">
          <MatchList
            groupMatches={data.groupMatches}
            knockoutMatches={data.knockoutMatches}
            teams={data.teams}
            venues={data.venues}
          />
        </div>
      )}

      {/* Footer - Hidden on mobile in bracket view for more space */}
      <footer className={`bg-gray-100 border-t border-gray-200 px-4 py-2 text-center text-xs text-gray-500 shrink-0 ${viewMode === 'bracket' ? 'hidden sm:block' : ''}`}>
        June 11 - July 19, 2026 • USA, Mexico & Canada
      </footer>
    </div>
  );
}

function App() {
  return (
    <PredictionProvider>
      <AppContent />
    </PredictionProvider>
  );
}

export default App;
